import { ReactNode, useMemo } from 'react';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { CallInsightApi, useCallInsightController } from '@frontend/call-intel-insights';
import { contextFactory } from '@frontend/design-system';

export type CallInsightPanelContextStore = {
  accordions: string | null;
  allCalls: CallIntelTypes.Call[];
  call: CallIntelTypes.Call;
  editedCallIds: string[];
  highlightedCitation: CallIntelTypes.HighlightedCitation;
  isDemoAccount: boolean;
  isFailedCall: boolean;
  isLoadingCallMetadata: boolean;
  isPoorAudio: boolean;
  isProcessingCall: boolean;
  isServiceQualityEnabled: boolean;
  isServiceQualityLoading: boolean;
  isSkippedCall: boolean;
  maxHeight: number | undefined;
  nextCall: CallIntelTypes.Call | null;
  previousCall: CallIntelTypes.Call | null;
  selectedCall: CallIntelTypes.Call | null;
  selectedCallMetaData: CallIntelTypes.CallMetadata | null;
  selectedDemoTasks?: CallIntelTypes.CallTaskListResponse | null;
  serviceQualityRes: CallIntelTypes.ServiceQualityResponse | undefined;
  source: CallIntelTypes.PanelSource;
  unsavedEditChangesConfig: CallIntelTypes.UnsavedEditChangesConfig;
  closeContext: CallInsightApi['close'];
  handleNextCall: CallInsightApi['handleNextClick'];
  handlePreviousCall: CallInsightApi['handlePreviousClick'];
  handleAccordionChange: (value: string | null) => void;
  handleSelectingCitationTime: (citationtext: string, citationTime: string) => void;
  onClose?: () => void;
  refetchCallMetadata: () => void;
  refetchServiceQuality: () => void;
  setAccordions: (value: string | null) => void;
  setHighlightedCitation: (value: CallIntelTypes.HighlightedCitation) => void;
  setMaxHeight: (value: number | undefined) => void;
  setAllCalls: CallInsightApi['setAllCalls'];
  setEditedCallIds: CallInsightApi['setEditedCallIds'];
  setSelectedCall: CallInsightApi['setSelectedCall'];
  setSelectedCallMetaData: CallInsightApi['setSelectedCallMetaData'];
  setUnsavedEditChangesConfig: CallInsightApi['setUnsavedEditChangesConfig'];
};

export const [CallInsightPanelContext, useCallInsightPanelContext] = contextFactory<CallInsightPanelContextStore>(
  'useCallInsightPanelContext must be used within a CallTakeawayPanelProvider'
);

type CallInsightPanelProviderProps = {
  children: ReactNode;
  value: Omit<
    CallInsightPanelContextStore,
    | 'closeContext'
    | 'handleNextCall'
    | 'handlePreviousCall'
    | 'setSelectedCall'
    | 'setSelectedCallMetaData'
    | 'setUnsavedEditChangesConfig'
  >;
};

export const CallTakeawayPanelProvider = ({ value, children }: CallInsightPanelProviderProps) => {
  const controller = useCallInsightController({ source: value.source });

  const contextValue: CallInsightPanelContextStore = useMemo(
    () => ({
      ...value,
      closeContext: controller.close,
      handleNextCall: controller.handleNextClick,
      handlePreviousCall: controller.handlePreviousClick,
      setAllCalls: controller.setAllCalls,
      setEditedCallIds: controller.setEditedCallIds,
      setSelectedCall: controller.setSelectedCall,
      setSelectedCallMetaData: controller.setSelectedCallMetaData,
      setUnsavedEditChangesConfig: controller.setUnsavedEditChangesConfig,
    }),
    [
      controller.close,
      controller.handleNextClick,
      controller.handlePreviousClick,
      controller.setAllCalls,
      controller.setEditedCallIds,
      controller.setSelectedCall,
      controller.setSelectedCallMetaData,
      controller.setUnsavedEditChangesConfig,
      value,
    ]
  );

  return <CallInsightPanelContext.Provider value={contextValue}>{children}</CallInsightPanelContext.Provider>;
};
